// ======================================================
// NK REFINERY
// ECONOMICS ENGINE
// NK-RBIC BUSINESS INTELLIGENCE CORE
// ======================================================

import {
    useContext,
    useMemo
} from "react";


import { ProcessContext } from "../context/ProcessContext";
import { PumpContext } from "../context/PumpContext";



export default function EconomicsEngine() {


    const {

        processData = {},

        currency,

        setCurrency,

        currencySymbol,

        convertCurrency


    } = useContext(ProcessContext);
    

    const {


        pumpData = {}


    } = useContext(PumpContext);



    // ======================================================
    // LIVE PROCESS TAGS
    // ======================================================


    const {

        FIC_2101 = 0,

        FIC_3401 = 0,

        FIC_3501 = 0,

        FIC_3601 = 0,

        FIC_36011 = 0,

        FIC_3701 = 0,

        FIC_3801 = 0


    } = processData;



    // ======================================================
    // EQUIPMENT LIVE STATUS
    // ======================================================


    const runningPumps =

        Object.values(pumpData)

        .filter(

            pump =>
            pump?.running === true

        )

        .length;



    const stoppedPumps =

        Object.keys(pumpData).length -

        runningPumps;



    const plant = {


        name:

            "NK Refinery",


        capacity:

            30000,


        status:

            "Online"

    };




    // ======================================================
    // LOCAL STORAGE REPORTS
    // ======================================================


    const furnaceReport =

        JSON.parse(

            localStorage.getItem(
                "FURNACE_REPORT"
            )

        ) || {};



    const laboratoryReport =

        JSON.parse(

            localStorage.getItem(
                "LAB_REPORT"
            )

        ) || {};



    const blendingReport =

        JSON.parse(

            localStorage.getItem(
                "BLENDING_REPORT"
            )

        ) || {};






    // ======================================================
    // CONSTANTS
    // ======================================================


    const BPM_TO_BPD = 1440;

    const DAYS_PER_MONTH = 30;

    const DAYS_PER_YEAR = 365;



// ======================================================
// LIVE TAG READER
// ======================================================

const FLOW_THRESHOLD = 0.06;

const readTag = (tag) => {

    let value = Number(processData?.[tag]);

    if (isNaN(value)) {
        value = Number(localStorage.getItem(tag));
    }

    if (isNaN(value) || value < FLOW_THRESHOLD) {
        return 0;
    }

    return value;

};

// ======================================================
// LIVE FLOWS
// ======================================================

const crudeFlow =
    readTag("FIC_2101");

const lpgFlow =
    readTag("FIC_3801");

const naphthaFlow =
    readTag("FIC_3501");

const keroseneFlow =
    readTag("FIC_3701");

const residueFlow =
    readTag("FIC_3401");

const dieselFlow =
    readTag("FIC_3601") +
    readTag("FIC_36011");


    const calculateProduction = (name, flow) => {

    const daily = flow * BPM_TO_BPD;

    const todayKey = `ECON_${name}_TODAY`;
    const monthKey = `ECON_${name}_MONTH`;
    const yearKey = `ECON_${name}_YEAR`;

    let cumulativeDaily =
        Number(localStorage.getItem(todayKey)) || 0;

    let cumulativeMonthly =
        Number(localStorage.getItem(monthKey)) || 0;

    let cumulativeYearly =
        Number(localStorage.getItem(yearKey)) || 0;

    if (flow > 0) {

        cumulativeDaily += flow;

        cumulativeMonthly += flow;

        cumulativeYearly += flow;

        localStorage.setItem(todayKey, cumulativeDaily);
        localStorage.setItem(monthKey, cumulativeMonthly);
        localStorage.setItem(yearKey, cumulativeYearly);
    }

    return {

        flow,

        unit: "bbl/min",

        daily,

        monthly: daily * DAYS_PER_MONTH,

        yearly: daily * DAYS_PER_YEAR,

        cumulativeDaily,

        cumulativeMonthly,

        cumulativeYearly

    };

};

const production = {

    Crude:
        calculateProduction("Crude", crudeFlow),

    LPG:
        calculateProduction("LPG", lpgFlow),

    Naphtha:
        calculateProduction("Naphtha", naphthaFlow),

    Kerosene:
        calculateProduction("Kerosene", keroseneFlow),

    Diesel:
        calculateProduction("Diesel", dieselFlow),

    Residue:
        calculateProduction("Residue", residueFlow)

};







    // ======================================================
    // PRODUCT SELLING PRICE
    // USD / BARREL
    // ======================================================


    const productPrices = {


        LPG:

            75,


        Naphtha:

            82,


        Kerosene:

            97,


        Diesel:

            112,


        Residue:

            55


    };






    // ======================================================
    // REVENUE CALCULATION
    // ======================================================


    const revenue = {};



    Object.keys(productPrices)

    .forEach(product=>{


        revenue[product] = {


            price:

                productPrices[product],



            daily:

                production[product].daily *

                productPrices[product],



            monthly:

                production[product].monthly *

                productPrices[product],



            yearly:

                production[product].yearly *

                productPrices[product]


        };


    });






    revenue.Total = {


        daily:

            Object.values(revenue)

            .reduce(

                (sum,item)=>

                    sum +

                    (

                        item.daily || 0

                    ),

                0

            ),



        monthly:

            Object.values(revenue)

            .reduce(

                (sum,item)=>

                    sum +

                    (

                        item.monthly || 0

                    ),

                0

            ),



        yearly:

            Object.values(revenue)

            .reduce(

                (sum,item)=>

                    sum +

                    (

                        item.yearly || 0

                    ),

                0

            )

    };







    // ======================================================
    // UTILITY COST MODEL
    // ======================================================


    const utilityPrices = {


        crudeOil:

            68,


        fuelGas:

            6.5,


        fuelOil:

            72,


        electricity:

            0.12,


        coolingWater:

            0.04,


        boilerWater:

            0.03,


        instrumentAir:

            0.015,


        nitrogen:

            0.08


    };





    const utilitiesConsumption = {


        crudeFeed:

            production.Crude.daily,



        fuelGas:

            Number(
                processData.FIC_2105 || 0
            ),



        fuelOil:

            Number(
                processData.FIC_2107 || 0
            ),



        electricity:

            4200,



        coolingWater:

            1800,



        boilerWater:

            320,



        instrumentAir:

            950,



        nitrogen:

            120


    };






    const crudeCost = {


        daily:

            utilitiesConsumption.crudeFeed *

            utilityPrices.crudeOil,



        monthly:

            utilitiesConsumption.crudeFeed *

            utilityPrices.crudeOil *

            DAYS_PER_MONTH,



        yearly:

            utilitiesConsumption.crudeFeed *

            utilityPrices.crudeOil *

            DAYS_PER_YEAR


    };







    const utilities = {


        fuelGas:

            utilitiesConsumption.fuelGas *

            utilityPrices.fuelGas,



        fuelOil:

            utilitiesConsumption.fuelOil *

            utilityPrices.fuelOil,



        electricity:

            utilitiesConsumption.electricity *

            utilityPrices.electricity,



        coolingWater:

            utilitiesConsumption.coolingWater *

            utilityPrices.coolingWater,



        boilerWater:

            utilitiesConsumption.boilerWater *

            utilityPrices.boilerWater,



        instrumentAir:

            utilitiesConsumption.instrumentAir *

            utilityPrices.instrumentAir,



        nitrogen:

            utilitiesConsumption.nitrogen *

            utilityPrices.nitrogen


    };



    utilities.totalDaily =

        utilities.fuelGas +

        utilities.fuelOil +

        utilities.electricity +

        utilities.coolingWater +

        utilities.boilerWater +

        utilities.instrumentAir +

        utilities.nitrogen;



    utilities.totalMonthly =

        utilities.totalDaily *

        DAYS_PER_MONTH;



    utilities.totalYearly =

        utilities.totalDaily *

        DAYS_PER_YEAR;







    // ======================================================
    // EQUIPMENT OPERATING COST
    // ======================================================


    const ELECTRICITY_COST_PER_KWH =

        0.18;



    const AVERAGE_PUMP_POWER =

        22;



    const pumpPower =

        runningPumps *

        AVERAGE_PUMP_POWER;



    const pumpEnergyDaily =

        pumpPower *

        24;



    const equipment = {


        runningPumps,


        stoppedPumps,


        pumpPower,



        totalDaily:

            pumpEnergyDaily *

            ELECTRICITY_COST_PER_KWH,



        totalMonthly:

            pumpEnergyDaily *

            DAYS_PER_MONTH *

            ELECTRICITY_COST_PER_KWH,



        totalYearly:

            pumpEnergyDaily *

            DAYS_PER_YEAR *

            ELECTRICITY_COST_PER_KWH


    };

        // ======================================================
    // PROCESS LOSSES
    // ======================================================


    const totalProductsDaily =

        production.LPG.daily +

        production.Naphtha.daily +

        production.Kerosene.daily +

        production.Diesel.daily +

        production.Residue.daily;



    const crudeFeedDaily =

        production.Crude.daily;



    const unaccountedLossDaily =

        Math.max(

            crudeFeedDaily -
            totalProductsDaily,

            0

        );



    const lossPercent =

        crudeFeedDaily > 0

        ?

        (

            unaccountedLossDaily /

            crudeFeedDaily

        ) * 100

        :

        0;



    const averageProductValue =

        revenue.Total.daily /

        Math.max(

            totalProductsDaily,

            1

        );



    const losses = {


        crudeFeedDaily,


        totalProductsDaily,


        unaccountedLossDaily,


        lossPercent,



        monthlyLoss:

            unaccountedLossDaily *
            DAYS_PER_MONTH,



        yearlyLoss:

            unaccountedLossDaily *
            DAYS_PER_YEAR,



        lossValue:{


            daily:

                unaccountedLossDaily *
                averageProductValue,



            monthly:

                unaccountedLossDaily *
                averageProductValue *
                DAYS_PER_MONTH,



            yearly:

                unaccountedLossDaily *
                averageProductValue *
                DAYS_PER_YEAR


        }


    };







    // ======================================================
    // QUALITY INFORMATION
    // ======================================================


    const quality = {


        LPG:{


            status:

                laboratoryReport?.LPG?.status ||
                "Unknown",


            sulfur:

                laboratoryReport?.LPG?.sulfur || 0,


            octane:

                laboratoryReport?.LPG?.octane || 0


        },



        Naphtha:{


            status:

                laboratoryReport?.Naphtha?.status ||
                "Unknown",


            octane:

                laboratoryReport?.Naphtha?.octane || 0,


            sulfur:

                laboratoryReport?.Naphtha?.sulfur || 0


        },



        Kerosene:{


            status:

                laboratoryReport?.Kerosene?.status ||
                "Unknown",


            smokePoint:

                laboratoryReport?.Kerosene?.smokePoint || 0,


            flashPoint:

                laboratoryReport?.Kerosene?.flashPoint || 0


        },



        Diesel:{


            status:

                laboratoryReport?.Diesel?.status ||
                "Unknown",


            cetane:

                laboratoryReport?.Diesel?.cetane || 0,


            sulfur:

                laboratoryReport?.Diesel?.sulfur || 0


        },



        Residue:{


            status:

                laboratoryReport?.Residue?.status ||
                "Unknown",


            viscosity:

                laboratoryReport?.Residue?.viscosity || 0


        }


    };








    // ======================================================
    // PROFITABILITY
    // ======================================================


    const grossRevenue =

        revenue.Total.daily;



    const totalOperatingCost =


        crudeCost.daily +

        utilities.totalDaily +

        equipment.totalDaily;



    const netProfit =


        grossRevenue -

        totalOperatingCost;



    const operatingMargin =


        grossRevenue > 0


        ?


        (

            netProfit /

            grossRevenue


        ) * 100


        :


        0;



    const profitability = {


        grossRevenue,


        totalOperatingCost,


        netProfit,


        operatingMargin,



        dailyProfit:

            netProfit,



        monthlyProfit:

            netProfit *
            DAYS_PER_MONTH,



        yearlyProfit:

            netProfit *
            DAYS_PER_YEAR,



        status:


            operatingMargin >= 25

            ?

            "Excellent"


            :


            operatingMargin >= 15

            ?

            "Healthy"


            :


            operatingMargin >= 5

            ?

            "Low"


            :

            "Loss"


    };








    // ======================================================
    // FORECAST MODEL
    // ======================================================


    const forecast = {



        production:{


            tomorrow:

                production.Crude.daily,


            sevenDays:

                production.Crude.daily * 7,


            monthly:

                production.Crude.daily *
                DAYS_PER_MONTH,


            yearly:

                production.Crude.daily *
                DAYS_PER_YEAR


        },




        revenue:{


            tomorrow:

                revenue.Total.daily,


            sevenDays:

                revenue.Total.daily * 7,


            monthly:

                revenue.Total.monthly,


            yearly:

                revenue.Total.yearly


        },




        utilities:{


            tomorrow:

                utilities.totalDaily,


            sevenDays:

                utilities.totalDaily * 7,


            monthly:

                utilities.totalMonthly,


            yearly:

                utilities.totalYearly


        },




        operatingCost:{


            tomorrow:

                totalOperatingCost,


            sevenDays:

                totalOperatingCost * 7,


            monthly:

                totalOperatingCost *
                DAYS_PER_MONTH,


            yearly:

                totalOperatingCost *
                DAYS_PER_YEAR


        },




        profit:{


            tomorrow:

                profitability.dailyProfit,


            sevenDays:

                profitability.dailyProfit * 7,


            monthly:

                profitability.monthlyProfit,


            yearly:

                profitability.yearlyProfit


        },




        capacity:{


            design:

                plant.capacity,



            actual:

                production.Crude.daily,



            utilization:


                (

                    production.Crude.daily /

                    plant.capacity

                ) * 100


        }


    };







    // ======================================================
    // SCENARIO ANALYSIS
    // ======================================================


    const scenario = {


        crudePriceIncrease10:{


            profit:


                revenue.Total.daily -

                (

                    crudeCost.daily * 1.10

                ) -

                utilities.totalDaily -

                equipment.totalDaily


        },



        crudePriceDecrease10:{


            profit:


                revenue.Total.daily -

                (

                    crudeCost.daily * 0.90

                ) -

                utilities.totalDaily -

                equipment.totalDaily


        },



        throughputIncrease10:{


            production:

                production.Crude.daily * 1.10,


            revenue:

                revenue.Total.daily * 1.10,


            profit:


                (

                    revenue.Total.daily * 1.10

                )

                -

                (

                    crudeCost.daily * 1.10

                )

                -

                utilities.totalDaily

                -

                equipment.totalDaily


        },



        throughputDecrease10:{


            production:

                production.Crude.daily * 0.90,


            revenue:

                revenue.Total.daily * 0.90,


            profit:


                (

                    revenue.Total.daily * 0.90

                )

                -

                (

                    crudeCost.daily * 0.90

                )

                -

                utilities.totalDaily

                -

                equipment.totalDaily


        }


    };







    // ======================================================
    // ECONOMETRICS / BUSINESS INTELLIGENCE KPI
    // ======================================================


    const totalProductRate =


        totalProductsDaily;



    const productYield =


        crudeFeedDaily > 0


        ?


        (

            totalProductRate /

            crudeFeedDaily


        ) * 100


        :


        0;




    const grossRefiningMargin =


        revenue.Total.daily -

        crudeCost.daily;





    const operatingCostPerBarrel =


        crudeFeedDaily > 0


        ?


        totalOperatingCost /

        crudeFeedDaily


        :


        0;





    const revenuePerBarrel =


        crudeFeedDaily > 0


        ?


        revenue.Total.daily /

        crudeFeedDaily


        :


        0;





    const profitPerBarrel =


        crudeFeedDaily > 0


        ?


        profitability.netProfit /

        crudeFeedDaily


        :


        0;





    const refineryUtilization =


        (

            production.Crude.daily /

            plant.capacity

        ) * 100;





    const econometrics = {


        totalProductRate,


        productYield,


        grossRefiningMargin,


        operatingMargin,


        operatingCostPerBarrel,


        revenuePerBarrel,


        profitPerBarrel,


        refineryUtilization


    };



// ======================================================
// REVENUE TREND
// ======================================================

const revenueTrend = [

    {

        period: "Today",

        revenue: revenue.Total.daily

    },

    {

        period: "Month",

        revenue: revenue.Total.monthly

    },

    {

        period: "Year",

        revenue: revenue.Total.yearly

    }

];



    // ======================================================
    // FINAL RETURN
    // ======================================================


    return useMemo(()=>({

       
        processData,


        plant,


        furnaceReport,


        laboratoryReport,


        blendingReport,



        production,


        productPrices,


        revenue,



        currency,

        setCurrency,

        currencySymbol,

        convertCurrency,



        crudeCost,


        utilities,


        equipment,


        losses,


        quality,


        profitability,


        forecast,


        scenario,


        econometrics,

        revenueTrend,



    }),[
processData,
currency,
setCurrency,
currencySymbol,
convertCurrency,


    ]);

    

}